import { PanelBody, ResetButton } from '@redfox-thank-you/components';
import {
	BackgroundAttributes,
	BackgroundControl,
	ResponsiveBreakpoint,
	SpacingAttributes,
	SpacingControl,
	TypographyAttributes,
	TypographyControl,
} from '@redfox-thank-you/control-groups';
import { BaseControl } from '@redfox-thank-you/controls';
import { InspectorControls } from '@wordpress/block-editor';
import {
	__experimentalUnitControl as UnitControl,
	SelectControl,
} from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

import type {
	LayoutSettings,
	ResponsiveLayoutAttributes,
	SettingsProps,
} from './types';

const flexDirectionOptions = [
	{ label: __( 'Row', 'redfox-thank-you' ), value: 'row' },
	{ label: __( 'Row Reverse', 'redfox-thank-you' ), value: 'row-reverse' },
];

const flexWrapOptions = [
	{ label: __( 'No Wrap', 'redfox-thank-you' ), value: 'nowrap' },
	{ label: __( 'Wrap', 'redfox-thank-you' ), value: 'wrap' },
	{ label: __( 'Wrap Reverse', 'redfox-thank-you' ), value: 'wrap-reverse' },
];

const justifyContentOptions = [
	{ label: __( 'Flex Start', 'redfox-thank-you' ), value: 'flex-start' },
	{ label: __( 'Flex End', 'redfox-thank-you' ), value: 'flex-end' },
	{ label: __( 'Center', 'redfox-thank-you' ), value: 'center' },
	{
		label: __( 'Space Between', 'redfox-thank-you' ),
		value: 'space-between',
	},
	{ label: __( 'Space Around', 'redfox-thank-you' ), value: 'space-around' },
	{ label: __( 'Space Evenly', 'redfox-thank-you' ), value: 'space-evenly' },
];

const alignItemsOptions = [
	{ label: __( 'Stretch', 'redfox-thank-you' ), value: 'stretch' },
	{ label: __( 'Flex Start', 'redfox-thank-you' ), value: 'flex-start' },
	{ label: __( 'Flex End', 'redfox-thank-you' ), value: 'flex-end' },
	{ label: __( 'Center', 'redfox-thank-you' ), value: 'center' },
	{ label: __( 'Baseline', 'redfox-thank-you' ), value: 'baseline' },
];

const gapUnits = [
	{ value: 'px', label: __( 'px', 'redfox-thank-you' ), default: 0 },
	{ value: 'em', label: __( 'em', 'redfox-thank-you' ), default: 0 },
	{ value: 'rem', label: __( 'rem', 'redfox-thank-you' ), default: 0 },
	{ value: '%', label: __( '%', 'redfox-thank-you' ), default: 0 },
	{ value: 'vw', label: __( 'vw', 'redfox-thank-you' ), default: 0 },
	{ value: 'vh', label: __( 'vh', 'redfox-thank-you' ), default: 0 },
];

/**
 * Settings component for Inline Container block
 */
export const Settings: React.FC< SettingsProps > = ( {
	updateAttributes,
	getAttribute,
} ) => {
	// Get WordPress editor device type for responsive controls
	const deviceType = useSelect( ( select ) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getDeviceType?.() ?? 'Desktop';
	}, [] );

	// Map WordPress device types to our breakpoint types
	const deviceTypeToBreakpoint = ( type: string ): ResponsiveBreakpoint => {
		switch ( type ) {
			case 'Tablet':
				return 'tablet';
			case 'Mobile':
				return 'mobile';
			default:
				return 'desktop';
		}
	};

	const currentBreakpoint = deviceTypeToBreakpoint( deviceType as string );

	// Get current layout settings for active breakpoint
	const layoutSettings = getAttribute< LayoutSettings >(
		`settings.layout.${ currentBreakpoint }`,
		{},
	);

	// Extract individual layout properties with defaults
	const flexDirection = layoutSettings.flexDirection || 'row';
	const flexWrap = layoutSettings.flexWrap || 'nowrap';
	const justifyContent = layoutSettings.justifyContent || 'flex-start';
	const alignItems = layoutSettings.alignItems || 'stretch';
	const rowGap = layoutSettings.rowGap || '';
	const columnGap = layoutSettings.columnGap || '';

	// Helper function to update layout settings for current breakpoint
	const updateLayoutSetting = (
		property: keyof LayoutSettings,
		value: string | undefined,
	) => {
		const currentSettings = getAttribute< LayoutSettings >(
			`settings.layout.${ currentBreakpoint }`,
			{},
		);

		updateAttributes( `settings.layout.${ currentBreakpoint }`, {
			...currentSettings,
			[ property ]: value,
		} );
	};

	// Helper function to reset layout settings for current breakpoint (unused, keeping for potential future use)
	// const resetLayoutSettings = () => {
	// 	updateAttributes( `settings.layout.${currentBreakpoint}`, {} );
	// };

	// Check if any layout values are set for current breakpoint (unused, keeping for potential future use)
	// const hasLayoutValues = Object.keys( layoutSettings ).length > 0;

	// Helper functions for specific attribute types
	const updateTypography = (
		typographyAttributes: Partial< TypographyAttributes >,
	) => {
		updateAttributes( 'style.block.text', typographyAttributes );
	};

	const updateBackground = (
		backgroundAttributes: Partial< BackgroundAttributes >,
	) => {
		updateAttributes( 'style.block.background', backgroundAttributes );
	};

	const updateSpacing = (
		spacingAttributes: Partial< SpacingAttributes >,
	) => {
		updateAttributes( 'style.block.spacing', spacingAttributes );
	};

	// Extract style attributes for controls
	const blockTextStyle = getAttribute( 'style.block.text', {} );
	const blockBackgroundStyle = getAttribute( 'style.block.background', {} );
	const blockSpacingStyle = getAttribute( 'style.block.spacing', {} );

	// Check if any layout values exist across all breakpoints
	const hasAnyLayoutValues = (): boolean => {
		const layout = getAttribute(
			'settings.layout',
			{},
		) as ResponsiveLayoutAttributes;
		if ( ! layout || typeof layout !== 'object' ) return false;

		return ( [ 'desktop', 'tablet', 'mobile' ] as const ).some(
			( breakpoint ) => {
				const settings = layout[ breakpoint ];
				return (
					settings &&
					typeof settings === 'object' &&
					( settings.rowGap ||
						settings.columnGap ||
						settings.flexDirection ||
						settings.flexWrap ||
						settings.justifyContent ||
						settings.alignItems )
				);
			},
		);
	};

	// Reset all layout settings
	const resetAllLayoutSettings = () => {
		updateAttributes( 'settings.layout', {
			desktop: {},
			tablet: {},
			mobile: {},
		} );
	};

	// Render header actions (reset button)
	const renderHeaderActions = () => {
		return (
			<ResetButton
				onReset={ resetAllLayoutSettings }
				show={ hasAnyLayoutValues() }
				label={ __( 'Reset Layout', 'redfox-thank-you' ) }
				className="redfox-thank-you-inline-container-settings__reset"
			/>
		);
	};

	return (
		<>
			<InspectorControls group="styles">
				{ /* Layout Settings */ }
				<PanelBody
					title={ __( 'Layout Settings', 'redfox-thank-you' ) }
					initialOpen={ true }
					showResponsive={ true }
					responsiveProps={ { showLabels: false } }
					headerActions={ renderHeaderActions() }
				>
					<BaseControl
						label={ __( 'Flex Direction', 'redfox-thank-you' ) }
						help={ __(
							'Sets the direction of flex items.',
							'redfox-thank-you',
						) }
						showResetButton={ true }
						hasValue={ flexDirection !== 'row' }
						onReset={ () =>
							updateLayoutSetting( 'flexDirection', 'row' )
						}
					>
						<SelectControl
							value={ flexDirection }
							options={ flexDirectionOptions }
							onChange={ ( value ) =>
								updateLayoutSetting( 'flexDirection', value )
							}
						/>
					</BaseControl>

					<BaseControl
						label={ __( 'Flex Wrap', 'redfox-thank-you' ) }
						help={ __(
							'Controls whether flex items wrap to new lines.',
							'redfox-thank-you',
						) }
						showResetButton={ true }
						hasValue={ flexWrap !== 'nowrap' }
						onReset={ () =>
							updateLayoutSetting( 'flexWrap', 'nowrap' )
						}
					>
						<SelectControl
							value={ flexWrap }
							options={ flexWrapOptions }
							onChange={ ( value ) =>
								updateLayoutSetting( 'flexWrap', value )
							}
						/>
					</BaseControl>

					<BaseControl
						label={ __( 'Justify Content', 'redfox-thank-you' ) }
						help={ __(
							'Aligns items along the main axis.',
							'redfox-thank-you',
						) }
						showResetButton={ true }
						hasValue={ justifyContent !== 'flex-start' }
						onReset={ () =>
							updateLayoutSetting(
								'justifyContent',
								'flex-start',
							)
						}
					>
						<SelectControl
							value={ justifyContent }
							options={ justifyContentOptions }
							onChange={ ( value ) =>
								updateLayoutSetting( 'justifyContent', value )
							}
						/>
					</BaseControl>

					<BaseControl
						label={ __( 'Align Items', 'redfox-thank-you' ) }
						help={ __(
							'Aligns items along the cross axis.',
							'redfox-thank-you',
						) }
						showResetButton={ true }
						hasValue={ alignItems !== 'stretch' }
						onReset={ () =>
							updateLayoutSetting( 'alignItems', 'stretch' )
						}
					>
						<SelectControl
							value={ alignItems }
							options={ alignItemsOptions }
							onChange={ ( value ) =>
								updateLayoutSetting( 'alignItems', value )
							}
						/>
					</BaseControl>

					<BaseControl
						label={ __( 'Row Gap', 'redfox-thank-you' ) }
						help={ __(
							'Space between flex items in rows.',
							'redfox-thank-you',
						) }
						showResetButton={ true }
						hasValue={ !! rowGap }
						onReset={ () => updateLayoutSetting( 'rowGap', '' ) }
					>
						<UnitControl
							value={ rowGap }
							onChange={ ( value ) =>
								updateLayoutSetting( 'rowGap', value || '' )
							}
							units={ gapUnits }
							min={ 0 }
							step={ 1 }
							placeholder="10"
						/>
					</BaseControl>

					<BaseControl
						label={ __( 'Column Gap', 'redfox-thank-you' ) }
						help={ __(
							'Space between flex items in columns.',
							'redfox-thank-you',
						) }
						showResetButton={ true }
						hasValue={ !! columnGap }
						onReset={ () => updateLayoutSetting( 'columnGap', '' ) }
					>
						<UnitControl
							value={ columnGap }
							onChange={ ( value ) =>
								updateLayoutSetting( 'columnGap', value || '' )
							}
							units={ gapUnits }
							min={ 0 }
							step={ 1 }
							placeholder="20"
						/>
					</BaseControl>
				</PanelBody>

				{ /* Typography Settings */ }
				<TypographyControl
					title={ __( 'Text', 'redfox-thank-you' ) }
					initialOpen={ false }
					attributes={ blockTextStyle }
					setAttributes={ updateTypography }
					enabledControls={ {
						textAlign: false,
					} }
				/>

				{ /* Background Settings */ }
				<BackgroundControl
					title={ __( 'Background', 'redfox-thank-you' ) }
					initialOpen={ false }
					responsive={ true }
					attributes={ blockBackgroundStyle }
					setAttributes={ updateBackground }
					enabledControls={ {
						backgroundColor: true,
						backgroundImage: true,
						backgroundGradient: true,
						backgroundPattern: false,
						backgroundOverlay: true,
						advancedOptions: true,
						responsive: true,
					} }
				/>

				{ /* Spacing Settings */ }
				<SpacingControl
					title={ __( 'Spacing', 'redfox-thank-you' ) }
					initialOpen={ false }
					responsive={ true }
					attributes={ blockSpacingStyle }
					setAttributes={ updateSpacing }
					enabledControls={ {
						margin: true,
						padding: true,
						responsive: true,
					} }
				/>
			</InspectorControls>
		</>
	);
};
